const OPENSHEET_ORIGIN = 'https://opensheet.elk.sh';
export const CATALOG_FALLBACK_URL = '/channels.json';

export const DEFAULT_OPENSHEET_TAB_NAMES = ['Commercials', 'Music Videos', 'Fiction'];

function toStringOrEmpty(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

export function normalizeUploadDate(rawValue) {
  const trimmed = toStringOrEmpty(rawValue).trim();
  if (!trimmed) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${trimmed}T00:00:00Z`;
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    if (trimmed.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(trimmed)) return trimmed;
    return `${trimmed}Z`;
  }

  return '';
}

export function sanitizeTitleAsSlug(title) {
  return toStringOrEmpty(title)
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function parseSpreadsheetIdFromOpenSheetUrl(opensheetUrl) {
  const trimmed = toStringOrEmpty(opensheetUrl).trim();
  if (!trimmed) return '';

  const googleSheetsMatch = trimmed.match(/docs\.google\.com\/spreadsheets\/d\/([^/?#]+)/i);
  if (googleSheetsMatch && googleSheetsMatch[1]) {
    return decodeURIComponent(googleSheetsMatch[1]);
  }

  try {
    const parsed = new URL(trimmed);
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts.length >= 1) return decodeURIComponent(parts[0]);
  } catch {
    // fall through to regex
  }

  const match = trimmed.match(/opensheet\.(?:elk\.sh|vercel\.app)\/([^/?#]+)/i);
  return match && match[1] ? decodeURIComponent(match[1]) : '';
}

export function parseTabNameFromOpenSheetUrl(opensheetUrl) {
  const trimmed = toStringOrEmpty(opensheetUrl).trim();
  if (!trimmed) return '';

  try {
    const parsed = new URL(trimmed);
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts.length >= 2) return decodeURIComponent(parts.slice(1).join('/'));
  } catch {
    // fall through
  }

  const match = trimmed.match(/opensheet\.(?:elk\.sh|vercel\.app)\/[^/?#]+\/([^/?#]+)/i);
  return match && match[1] ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : '';
}

export function parseTabNamesFromEnvironment({ tabNamesString } = {}) {
  const raw = toStringOrEmpty(tabNamesString).trim();
  if (!raw) return [];
  return raw
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
}

export function resolveOpenSheetTabNames({ opensheetUrl, tabNamesString } = {}) {
  const fromEnv = parseTabNamesFromEnvironment({ tabNamesString });
  if (fromEnv.length) return fromEnv;

  const spreadsheetId = parseSpreadsheetIdFromOpenSheetUrl(opensheetUrl);
  if (!spreadsheetId) {
    const tabFromUrl = parseTabNameFromOpenSheetUrl(opensheetUrl);
    return tabFromUrl ? [tabFromUrl] : [];
  }

  return [...DEFAULT_OPENSHEET_TAB_NAMES];
}

export function buildOpenSheetTabUrl(spreadsheetId, tabName) {
  const id = toStringOrEmpty(spreadsheetId).trim();
  const tab = toStringOrEmpty(tabName).trim();
  if (!id || !tab) throw new Error('Missing spreadsheet ID or tab name');
  return `${OPENSHEET_ORIGIN}/${encodeURIComponent(id)}/${encodeURIComponent(tab)}`;
}

export function normalizeChannelRows(rows) {
  const input = Array.isArray(rows) ? rows : [];
  const seenByCategory = new Map();

  const normalized = [];
  for (const row of input) {
    if (!row || typeof row !== 'object') continue;

    const legacySlugRaw = toStringOrEmpty(row.slug).trim();
    const muxIdRaw = toStringOrEmpty(row.muxId).trim();
    if (!muxIdRaw) continue;

    const title = toStringOrEmpty(row.title).trim();
    const description = toStringOrEmpty(row.description).trim();
    const uploadDate = normalizeUploadDate(row.uploadDate);
    const sheetName = toStringOrEmpty(row.name).trim();
    const categoryTitle = toStringOrEmpty(row.categoryTitle).trim();

    const baseSlug = sanitizeTitleAsSlug(title) || sanitizeTitleAsSlug(muxIdRaw);
    if (!baseSlug) continue;

    const legacySlug = legacySlugRaw || baseSlug;

    const categoryKey = categoryTitle || '__default__';
    if (!seenByCategory.has(categoryKey)) {
      seenByCategory.set(categoryKey, new Set());
    }
    const seen = seenByCategory.get(categoryKey);

    // Ensure unique slugs within each category even if the sheet has duplicates.
    let slug = baseSlug;
    let n = 2;
    while (seen.has(slug)) {
      slug = `${baseSlug}-${n}`;
      n += 1;
    }
    seen.add(slug);

    const channel = {
      slug,
      legacySlug: legacySlug === slug ? undefined : legacySlug,
      muxId: muxIdRaw,
      title: title || `CH ${normalized.length + 1}`,
      description
    };
    if (uploadDate) channel.uploadDate = uploadDate;
    if (sheetName) channel.sheetName = sheetName;
    if (categoryTitle) channel.categoryTitle = categoryTitle;
    normalized.push(channel);
  }

  return normalized;
}

async function fetchOpenSheetJson(url) {
  const response = await fetch(url, {
    headers: {
      Accept: 'application/json'
    }
  });
  if (!response.ok) {
    throw new Error(`OpenSheet request failed (${response.status})`);
  }
  return response.json();
}

export async function fetchChannelRowsFromOpenSheetTab(spreadsheetId, tabName) {
  const url = buildOpenSheetTabUrl(spreadsheetId, tabName);
  const data = await fetchOpenSheetJson(url);
  const rows = Array.isArray(data) ? data : [];
  return rows.map(row => ({
    ...row,
    categoryTitle: tabName
  }));
}

function buildCategoriesFromNormalizedRows(tabNames, normalizedRows) {
  const channelsByTitle = new Map();
  for (const row of normalizedRows) {
    const title = toStringOrEmpty(row.categoryTitle).trim() || 'Channels';
    if (!channelsByTitle.has(title)) channelsByTitle.set(title, []);
    channelsByTitle.get(title).push(row);
  }

  const categories = [];
  for (const tabName of tabNames) {
    if (tabName.startsWith('_')) continue;
    const channels = channelsByTitle.get(tabName) || [];
    if (!channels.length) continue;
    categories.push({ title: tabName, channels });
  }

  return categories;
}

export async function fetchCategorizedChannelsFromOpenSheet({ opensheetUrl, tabNames } = {}) {
  const url = toStringOrEmpty(opensheetUrl).trim();
  if (!url) throw new Error('Missing OpenSheet URL');

  const spreadsheetId = parseSpreadsheetIdFromOpenSheetUrl(url);
  if (!spreadsheetId) throw new Error('Could not parse spreadsheet ID from OpenSheet URL');

  const tabNamesList = resolveOpenSheetTabNames({
    opensheetUrl: url,
    tabNamesString: Array.isArray(tabNames) ? tabNames.join(',') : tabNames
  });

  if (!tabNamesList.length) {
    const data = await fetchOpenSheetJson(url);
    const channels = normalizeChannelRows(data);
    const title = parseTabNameFromOpenSheetUrl(url) || 'Channels';
    return {
      categories: channels.length ? [{ title, channels }] : [],
      channels
    };
  }

  if (tabNamesList.length === 1) {
    const singleTabName = tabNamesList[0];
    const singleTabUrl =
      parseTabNameFromOpenSheetUrl(url) === singleTabName
        ? url
        : buildOpenSheetTabUrl(spreadsheetId, singleTabName);
    const data = await fetchOpenSheetJson(singleTabUrl);
    const rows = Array.isArray(data) ? data : [];
    const taggedRows = rows.map(row => ({ ...row, categoryTitle: singleTabName }));
    const channels = normalizeChannelRows(taggedRows);
    return {
      categories: channels.length ? [{ title: singleTabName, channels }] : [],
      channels
    };
  }

  const combinedRows = [];
  for (const tabName of tabNamesList) {
    if (tabName.startsWith('_')) continue;
    try {
      const rows = await fetchChannelRowsFromOpenSheetTab(spreadsheetId, tabName);
      combinedRows.push(...rows);
    } catch (error) {
      console.warn(`OpenSheet tab "${tabName}" skipped:`, error && error.message ? error.message : error);
    }
  }

  const channels = normalizeChannelRows(combinedRows);
  const categories = buildCategoriesFromNormalizedRows(tabNamesList, channels);

  return { categories, channels };
}

export async function fetchChannelsFromOpenSheet(opensheetUrl, options = {}) {
  const { channels } = await fetchCategorizedChannelsFromOpenSheet({
    opensheetUrl,
    tabNames: options.tabNames
  });
  return channels;
}

export async function fetchCategorizedChannelsFromJsonFallback(
  fallbackUrl = CATALOG_FALLBACK_URL
) {
  const response = await fetch(fallbackUrl, {
    headers: {
      Accept: 'application/json'
    }
  });
  if (!response.ok) {
    throw new Error(`Channel catalog fallback failed (${response.status})`);
  }

  const data = await response.json();
  const channels = normalizeChannelRows(data?.channels);
  if (!channels.length) {
    throw new Error('Channel catalog fallback contains no valid channels');
  }

  let categories = [];
  if (Array.isArray(data?.categories) && data.categories.length) {
    categories = data.categories
      .map(category => ({
        title: toStringOrEmpty(category?.title).trim() || 'Channels',
        channels: normalizeChannelRows(category?.channels)
      }))
      .filter(category => category.channels.length);
  }
  if (!categories.length) {
    categories = [{ title: 'Channels', channels }];
  }

  return { categories, channels };
}

export async function fetchCategorizedChannelsWithFallback({
  opensheetUrl,
  tabNames,
  fallbackUrl = CATALOG_FALLBACK_URL
} = {}) {
  try {
    const result = await fetchCategorizedChannelsFromOpenSheet({ opensheetUrl, tabNames });
    if (result?.channels?.length) return result;
    throw new Error('OpenSheet returned no valid channels');
  } catch (openSheetError) {
    console.warn(
      'OpenSheet unavailable, using channel catalog fallback:',
      openSheetError && openSheetError.message ? openSheetError.message : openSheetError
    );
    return fetchCategorizedChannelsFromJsonFallback(fallbackUrl);
  }
}
